import { useNavigate } from 'react-router-dom';
import { Mic, Headphones, Brain, Code, Users, ArrowRight, CheckCircle, BarChart3 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { useAnalytics } from '@/hooks/useAnalytics';
import SEOHead from '@/components/SEOHead';

export default function AudioMemoriesPage() {
  const navigate = useNavigate();
  const { trackEvent } = useAnalytics();

  const handleFeatureClick = (feature: string) => {
    trackEvent('roadmap_feature_detail_view', {
      feature: 'audio_memories',
      detail_section: feature,
      source: 'roadmap_landing_page'
    });
  };

  const handleTryAudioClick = (integration: string) => {
    trackEvent('audio_memories_cta_click', {
      integration,
      source: 'roadmap_landing_page'
    });
    
    if (integration === 'whatsapp') {
      navigate('/integrations/whatsapp-basic');
    } else if (integration === 'python') {
      navigate('/integrations/bare-api');
    }
  };

  return (
    <>
      <SEOHead 
        title="AI Audio Memory & Speech-to-Text"
        description="Add voice context to your AI applications with Memara's audio memory storage. Speech-to-text transcription, semantic audio search, and voice AI understanding for developers building intelligent applications."
        keywords={["AI audio memory", "speech to text API", "voice memory storage", "audio transcription", "semantic audio search", "voice AI development", "multimodal AI memory", "audio embeddings", "AI developer tools", "meeting transcription"]}
        canonicalUrl="https://memara.io/roadmap/audio-memories"
        ogType="article"
      />

      <div className="min-h-screen bg-gradient-to-br from-blue-50 via-white to-indigo-50">
        {/* Hero Section */}
        <section className="relative px-6 py-24 mx-auto max-w-7xl lg:px-8">
          <div className="text-center">
            <Badge className="mb-6 bg-green-100 text-green-700 hover:bg-green-200">
              <CheckCircle className="w-4 h-4 mr-2" />
              Available Now (Beta)
            </Badge>
            
            <h1 className="text-4xl font-bold tracking-tight text-gray-900 sm:text-6xl">
              AI Audio Memory
              <span className="block text-transparent bg-clip-text bg-gradient-to-r from-blue-600 to-indigo-600">
                & Speech Intelligence
              </span>
            </h1>
            
            <p className="mt-6 text-xl leading-8 text-gray-600 max-w-3xl mx-auto">
              Voice memories, transcribed and searchable, on paid plans. Starter includes 10 minutes
              per file, Pro 1 hour per file, and Enterprise 2 hours per file. Free is text only. Use the web app, REST API, or Python SDK.
              MCP clients and ChatGPT stay text only.
            </p>

            <div className="mt-10 flex flex-wrap items-center justify-center gap-x-6 gap-y-4">
              <Button 
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => {
                  trackEvent('audio_memories_cta_click', {
                    integration: 'memory_workspace',
                    source: 'roadmap_landing_page',
                  });
                  navigate('/spaces');
                }}
              >
                Capture in Memory Workspace
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => navigate('/signup')}
              >
                Start free
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => handleTryAudioClick('python')}
              >
                Try with Python SDK
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          </div>
        </section>

        {/* Technical Capabilities */}
        <section className="px-6 py-16 mx-auto max-w-7xl lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              AI-Powered Voice Memory Capabilities
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Built for developers who need intelligent audio processing and speech understanding
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2 lg:grid-cols-3">
            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleFeatureClick('speech_to_text')}>
              <CardHeader>
                <div className="w-12 h-12 bg-blue-100 rounded-lg flex items-center justify-center mb-4">
                  <Mic className="w-6 h-6 text-blue-600" />
                </div>
                <CardTitle>Speech-to-Text Transcription</CardTitle>
                <CardDescription>
                  High-accuracy speech recognition with speaker identification
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Multi-language speech recognition</li>
                  <li>• Speaker diarization and identification</li>
                  <li>• Timestamp-accurate transcriptions</li>
                  <li>• Technical vocabulary optimization</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleFeatureClick('semantic_audio_search')}>
              <CardHeader>
                <div className="w-12 h-12 bg-indigo-100 rounded-lg flex items-center justify-center mb-4">
                  <Headphones className="w-6 h-6 text-indigo-600" />
                </div>
                <CardTitle>Semantic Audio Search</CardTitle>
                <CardDescription>
                  Find audio content by meaning, not just keywords
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Search by conversation topics and themes</li>
                  <li>• Find specific speakers or voices</li>
                  <li>• Emotion and sentiment detection</li>
                  <li>• Audio similarity matching</li>
                </ul>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:shadow-lg transition-shadow" onClick={() => handleFeatureClick('voice_intelligence')}>
              <CardHeader>
                <div className="w-12 h-12 bg-green-100 rounded-lg flex items-center justify-center mb-4">
                  <Brain className="w-6 h-6 text-green-600" />
                </div>
                <CardTitle>Voice Intelligence</CardTitle>
                <CardDescription>
                  AI-powered audio analysis and understanding
                </CardDescription>
              </CardHeader>
              <CardContent>
                <ul className="space-y-2 text-sm text-gray-600">
                  <li>• Meeting summary generation</li>
                  <li>• Action item extraction</li>
                  <li>• Key decision identification</li>
                  <li>• Voice tone and mood analysis</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Developer Integration Examples */}
        <section className="px-6 py-16 mx-auto max-w-7xl lg:px-8 bg-gray-50">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Built for Voice-Enabled Applications
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Simple APIs that integrate seamlessly with your voice AI workflows
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 lg:grid-cols-2">
            <Card>
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <Code className="w-6 h-6 text-blue-600" />
                  <CardTitle>Store Audio Memories</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-900 rounded-lg p-4 text-sm">
                  <pre className="text-green-400">
{`// Store audio with context
await memara.createMemory({
  content: {
    text: "Client feedback session notes",
    audio: [meeting_recording]
  },
  tags: ["meeting", "client", "feedback"],
  space: "work_meetings",
  metadata: {
    speakers: ["John", "Sarah", "Mike"],
    duration: "45min"
  }
});`}
                  </pre>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader>
                <div className="flex items-center space-x-3">
                  <Headphones className="w-6 h-6 text-indigo-600" />
                  <CardTitle>Semantic Audio Search</CardTitle>
                </div>
              </CardHeader>
              <CardContent>
                <div className="bg-gray-900 rounded-lg p-4 text-sm">
                  <pre className="text-green-400">
{`// Find audio by content
const results = await memara.searchMemories({
  query: "discussions about pricing strategy",
  contentTypes: ["audio"],
  space: "work_meetings",
  filters: {
    speaker: "John",
    duration: { min: "30min" }
  }
});`}
                  </pre>
                </div>
              </CardContent>
            </Card>
          </div>
        </section>

        {/* Use Cases for AI Developers */}
        <section className="px-6 py-16 mx-auto max-w-7xl lg:px-8">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Perfect for Voice AI Development
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Real-world use cases that enhance your voice-enabled applications
            </p>
          </div>

          <div className="grid grid-cols-1 gap-8 md:grid-cols-2">
            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-blue-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Users className="w-4 h-4 text-blue-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Meeting Intelligence</h3>
                  <p className="text-gray-600">
                    Store and analyze team meetings, client calls, and interviews. 
                    AI can extract action items, decisions, and key insights automatically.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-indigo-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Code className="w-4 h-4 text-indigo-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Voice-Enabled Assistants</h3>
                  <p className="text-gray-600">
                    Build AI assistants that remember voice interactions. 
                    Context-aware responses based on previous conversations.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-green-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Brain className="w-4 h-4 text-green-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Customer Support Analysis</h3>
                  <p className="text-gray-600">
                    Analyze support calls for sentiment, issues, and resolution patterns. 
                    Improve service quality with voice data insights.
                  </p>
                </div>
              </div>
            </div>

            <div className="space-y-6">
              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-purple-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Mic className="w-4 h-4 text-purple-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Podcast & Content Analysis</h3>
                  <p className="text-gray-600">
                    Transcribe and analyze podcast episodes, interviews, and content. 
                    Extract key topics, quotes, and themes for content creation.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-orange-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <BarChart3 className="w-4 h-4 text-orange-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Training & Education</h3>
                  <p className="text-gray-600">
                    Store training sessions, lectures, and educational content. 
                    AI-powered search helps find specific topics and explanations.
                  </p>
                </div>
              </div>

              <div className="flex items-start space-x-4">
                <div className="w-8 h-8 bg-red-100 rounded-full flex items-center justify-center flex-shrink-0">
                  <Headphones className="w-4 h-4 text-red-600" />
                </div>
                <div>
                  <h3 className="font-semibold text-gray-900">Voice Data Research</h3>
                  <p className="text-gray-600">
                    Collect and analyze voice data for research projects. 
                    Speaker identification, emotion analysis, and pattern recognition.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Integration Platforms */}
        <section className="px-6 py-16 mx-auto max-w-7xl lg:px-8 bg-gray-50">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900">
              Works with Your Voice Platforms
            </h2>
            <p className="mt-4 text-lg text-gray-600">
              Audio memories are available across communication and automation platforms
            </p>
          </div>

          <div className="grid grid-cols-2 gap-8 md:grid-cols-4">
            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-lg shadow-md flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">💬</span>
              </div>
              <h3 className="font-medium">WhatsApp</h3>
              <p className="text-sm text-gray-500">Voice message storage</p>
              <Badge className="mt-2 bg-green-100 text-green-700">Available</Badge>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-lg shadow-md flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🐍</span>
              </div>
              <h3 className="font-medium">Python SDK</h3>
              <p className="text-sm text-gray-500">Audio memory API</p>
              <Badge className="mt-2 bg-green-100 text-green-700">Available</Badge>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-lg shadow-md flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🔧</span>
              </div>
              <h3 className="font-medium">REST API</h3>
              <p className="text-sm text-gray-500">Direct API access</p>
              <Badge className="mt-2 bg-green-100 text-green-700">Available</Badge>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 bg-white rounded-lg shadow-md flex items-center justify-center mx-auto mb-4">
                <span className="text-2xl">🎙️</span>
              </div>
              <h3 className="font-medium">More Coming</h3>
              <p className="text-sm text-gray-500">Additional platforms</p>
            </div>
          </div>
        </section>

        {/* CTA Section */}
        <section className="px-6 py-24 mx-auto max-w-7xl lg:px-8">
          <div className="text-center">
            <h2 className="text-3xl font-bold tracking-tight text-gray-900 mb-6">
              Ready to Build Voice-Intelligent Applications?
            </h2>
            <p className="text-lg text-gray-600 mb-8 max-w-2xl mx-auto">
              Join AI developers building smarter voice applications with Memara's memory platform.
            </p>
            
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Button 
                size="lg" 
                className="bg-blue-600 hover:bg-blue-700"
                onClick={() => handleTryAudioClick('whatsapp')}
              >
                Try Audio Memories with WhatsApp
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
              <Button 
                size="lg" 
                variant="outline"
                onClick={() => handleTryAudioClick('python')}
              >
                View Python SDK Documentation
              </Button>
            </div>
          </div>
        </section>
      </div>
    </>
  );
} 